// Comparator + math token primitives, shared by the wrap patterns below.
const COMP = '(?:=|<=?|>=?|≤|≥|≠|≈)';
// Single-letter math variable. We exclude `a` and `i` because they're
// extremely common English articles/pronouns and producing `$a$ rate` from
// "a rate" is a worse outcome than the missed wrap on the rare math `a`.
const VAR = '[b-hj-z]';
const NUM = '-?\\d+(?:\\.\\d+)?';
// Within compound terms (coefficients * variable) we allow ANY single letter,
// because "2a" or "3i" really is mathematical regardless of letter choice.
const COMPOUND_VAR = '[a-z]';
const TERM = `(?:${NUM})?${COMPOUND_VAR}`;

// Pass-through segments: already-delimited math AND any HTML tag.
// We never want to inject `$...$` inside `<b>`, `<span ...>`, or similar
// because the `b`, `p`, etc. inside those tags are HTML tokens, not
// math variables. Stripping HTML happens upstream in sanitizeRichHtml,
// but the wrap is also called by the backfill script against raw DB
// rows that may still contain legacy HTML — so we defend here too.
// Longest delimiters first so $$ wins over $.
const INLINE_MATH_SEG = /(\$\$[\s\S]*?\$\$|\$[^$\n]+\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]|<[^>]+>)/g;

/**
 * Wrap bare math fragments in `$...$` so mobile's MathJax renders them in
 * the same italic-TeX font as surrounding `$$...$$` blocks.
 *
 * Conservative: wraps full comparator expressions (`x ≤ -3`, `-2x ≤ 6`) and
 * lone single-letter variables, but only when the wider note already looks
 * math-flavored (has digits, operators, or existing math delimiters).
 *
 * Idempotent: running twice produces the same output as running once.
 */
export function wrapBareInlineMath(text) {
  if (!text) return '';

  const NOTE_IS_MATHY = /[\d=<>≤≥≠≈+*/]|\$|\\[(\\[a-zA-Z]/.test(text);

  const segments = text.split(INLINE_MATH_SEG);

  const wrap = (src) => {
    let out = src;

    // (1) comparator expressions: "x ≤ -3", "-2x ≤ 6", "2x + 3 = 7"
    const comparatorRe = new RegExp(
      `(?<![\\w$\\\\])(${TERM}(?:\\s*[+\\-*/]\\s*(?:${NUM}|${TERM}))*\\s*${COMP}\\s*(?:${NUM}|${TERM})(?:\\s*[+\\-*/]\\s*(?:${NUM}|${TERM}))*)(?![\\w$])`,
      'g'
    );
    out = out.replace(comparatorRe, '$$$1$$');

    // (2) lone variable as a standalone token. Only attempted when the note
    //     as a whole looks math-flavored, so plain English prose like
    //     "Press a to continue" is left alone.
    if (NOTE_IS_MATHY) {
      const loneVarRe = new RegExp(
        `(?<=[\\s,.;:(\\-+*/=<>≤≥≠≈]|^)(${VAR})(?=[\\s,.;:)\\-+*/=<>≤≥≠≈]|$)`,
        'g'
      );
      out = out.replace(loneVarRe, (match) => `$${match}$`);
    }

    return out;
  };

  return segments
    .map((seg, idx) => {
      if (idx % 2 === 1) return seg; // already-delimited, leave intact
      if (!seg) return seg;
      return wrap(seg);
    })
    .join('');
}
